import { useAtom } from "jotai";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import useWorkspace from "../workspaces/useWorkspace";
import { pageSettingsAtom } from "../../../atoms/PageAtoms";
import PageAPI from "../../api/PageAPI";
import { Page } from "../../../types/GlobalInterfaces";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import { ActivityEditedPage } from "../../../types/UserActivityInterfaces";

interface Props {
  pageID: string;
  payload: { payload: Partial<Page>; _UserActivity: ActivityEditedPage };
  onSuccess?: () => void;
}

const useUpdatePage = () => {
  const { workspace } = useWorkspace();
  const [selectedPage, setSelectedPage] = useAtom(pageSettingsAtom);
  const queryClient = useQueryClient();

  const updatePage = useMutation({
    mutationFn: ({ pageID, payload }: Props) =>
      PageAPI.updatePage(workspace.id, pageID, payload),
    onMutate: async ({ pageID, payload }) => {
      // Snapshot previous page in case of error
      const previousPage = queryClient.getQueryData<Page>(["page", pageID]);

      if (!previousPage) return { previousPage };

      const updatedPage = { ...previousPage, ...(payload?.payload ?? {}) };

      // Optimistically update!
      queryClient.setQueryData(["page", pageID], updatedPage);

      if (selectedPage?.id === updatedPage.id) {
        setSelectedPage(updatedPage);
      }

      await queryClient.cancelQueries({
        queryKey: ["page", pageID],
      });

      return { previousPage };
    },
    // Reset Page & SidebarItems on Error.
    onError: (_, { pageID }, context) => {
      queryClient.setQueryData(["page", pageID], context?.previousPage);
    },
    onSuccess: (_, { onSuccess }) => {
      callFunctionIfExists(onSuccess);
    },
    onSettled: (data, error, { pageID }) => {
      // Commenting this out to save on API requests for now.
      // But invalidating here ensures nothing is messed up from mutation above
      // queryClient.invalidateQueries(["page", pageID]);
    },
  });

  return { updatePage: updatePage.mutate, pageUpdating: updatePage.isPending };
};

export default useUpdatePage;
